import { existsSync } from "node:fs";
import type { AgentTool } from "@apholdings/jensen-agent-core";
import { type Static, Type } from "@sinclair/typebox";
import { spawn } from "child_process";
import { getShellConfig, getShellEnv, killProcessTree } from "../../utils/shell.js";
import type { BashEvidence, ResolvedBashResult } from "../bash-executor.js";
import { executeBashWithOperations } from "../bash-executor.js";
import { runForegroundProcess } from "../process-runner.js";
import { DEFAULT_MAX_BYTES, DEFAULT_MAX_LINES, type TruncationResult, truncateTail } from "./truncate.js";

/**
 * Maximum allowed timeout in seconds.
 * Values above this are clamped. This prevents excessively large timeouts.
 */
const MAX_TIMEOUT_SECONDS = 86400; // 24 hours

const bashSchema = Type.Object({
	command: Type.String({ description: "Bash command to execute" }),
	timeout: Type.Optional(Type.Number({ description: "Timeout in seconds (optional, no default timeout)" })),
});

export type BashToolInput = Static<typeof bashSchema>;

/**
 * Public details for the bash tool shown in the TUI and event stream.
 *
 * All fields added after 1.1.6 are optional in the public type for backward
 * compatibility with consumers that construct mocks, adapters, or fixtures
 * using the 1.1.6 shape. The runtime always produces every field.
 */
export interface BashToolDetails {
	// 1.1.6 surface
	truncation?: TruncationResult;
	fullOutputPath?: string;
	cancelled?: boolean;

	// --- Added in 1.1.7 (optional in public type; always present at runtime) ---
	command?: string;
	cwd?: string;
	stdout?: string;
	stderr?: string;
	exitCode?: number | undefined;
	startedAt?: string;
	finishedAt?: string;
	timedOut?: boolean;
	truncated?: boolean;
	spawnError?: string;
	evidence?: BashEvidence;
}

/**
 * Internal resolved details type. Every runtime-produced BashToolDetails
 * satisfies this contract. Internal consumers use this type so they can
 * rely on fields being present without optional chaining.
 */
export interface ResolvedBashToolDetails extends BashToolDetails {
	command: string;
	cwd: string;
	stdout: string;
	stderr: string;
	exitCode: number | undefined;
	startedAt: string;
	finishedAt: string;
	timedOut: boolean;
	cancelled: boolean;
	truncated: boolean;
}

export interface BashOperations {
	/**
	 * Execute a command and stream output.
	 * @param command - The command to execute
	 * @param cwd - Working directory
	 * @param options - Execution options
	 * @returns Promise resolving to exit code (null if killed) and optional pipeline data
	 */
	exec: (
		command: string,
		cwd: string,
		options: {
			/** Combined stdout + stderr stream (legacy, always called) */
			onData: (data: Buffer) => void;
			/** Separate stdout stream (optional, called alongside onData) */
			onStdout?: (data: Buffer) => void;
			/** Separate stderr stream (optional, called alongside onData) */
			onStderr?: (data: Buffer) => void;
			signal?: AbortSignal;
			timeout?: number;
			env?: NodeJS.ProcessEnv;
		},
	) => Promise<{ exitCode: number | null }>;
}

export function createLocalBashOperations(): BashOperations {
	return {
		exec: (command, cwd, { onData, onStdout, onStderr, signal, timeout, env }) => {
			return new Promise((resolve, reject) => {
				const { shell, args } = getShellConfig();

				if (!existsSync(cwd)) {
					reject(new Error(`Working directory does not exist: ${cwd}\nCannot execute bash commands.`));
					return;
				}

				// Execute user command directly — no preamble, no trap, no wrapper.
				// Pipeline detection is done heuristically at the call site
				// (executeBashWithOperations), not via injected instrumentation.
				const child = spawn(shell, [...args, command], {
					cwd,
					detached: true,
					env: env ?? getShellEnv(),
					stdio: ["ignore", "pipe", "pipe"],
				});

				runForegroundProcess({
					child,
					onStdout: (data) => {
						onData(data);
						onStdout?.(data);
					},
					onStderr: (data) => {
						onData(data);
						onStderr?.(data);
					},
					signal,
					timeout,
					kill: () => {
						if (child.pid) killProcessTree(child.pid);
					},
				})
					.then(resolve)
					.catch(reject);
			});
		},
	};
}

export interface BashSpawnContext {
	command: string;
	cwd: string;
	env: NodeJS.ProcessEnv;
}

export type BashSpawnHook = (context: BashSpawnContext) => BashSpawnContext;

function resolveSpawnContext(command: string, cwd: string, spawnHook?: BashSpawnHook): BashSpawnContext {
	const baseContext: BashSpawnContext = {
		command,
		cwd,
		env: { ...getShellEnv() },
	};

	return spawnHook ? spawnHook(baseContext) : baseContext;
}

export interface BashToolOptions {
	/** Custom operations for command execution. Default: local shell */
	operations?: BashOperations;
	/** Command prefix prepended to every command (e.g., "shopt -s expand_aliases" for alias support) */
	commandPrefix?: string;
	/** Hook to adjust command, cwd, or env before execution */
	spawnHook?: BashSpawnHook;
}

/**
 * Format BashResult into model-facing text content with structured evidence section.
 * The model sees: command, exit code, stdout, stderr, timing, and pipeline metadata.
 * TUI details carry the full BashResult for rendering.
 */
function formatBashResultForModel(
	result: ResolvedBashResult,
	command: string,
	cwd: string,
): { contentText: string; details: ResolvedBashToolDetails } {
	const lines: string[] = [];

	// Structured evidence header
	lines.push(`Command: \`${command}\``);
	lines.push(`CWD: ${cwd}`);

	const trimmedStdout = result.stdout.trimEnd();
	const trimmedStderr = result.stderr.trimEnd();

	if (trimmedStdout) {
		lines.push("");
		lines.push("stdout:");
		lines.push(trimmedStdout);
	}

	if (trimmedStderr) {
		lines.push("");
		lines.push("stderr:");
		lines.push(trimmedStderr);
	}

	if (!trimmedStdout && !trimmedStderr) {
		lines.push("");
		lines.push("(no output)");
	}

	const evidenceLines: string[] = [];
	evidenceLines.push(`exit_code: ${result.exitCode !== undefined ? result.exitCode : "undefined"}`);
	evidenceLines.push(`timed_out: ${result.timedOut}`);
	evidenceLines.push(`cancelled: ${result.cancelled}`);
	evidenceLines.push(`truncated: ${result.truncated}`);

	if (result.spawnError) {
		evidenceLines.push(`spawn_error: ${result.spawnError}`);
	}

	// Always render evidence metadata for every command
	evidenceLines.push(`exit_status_known: ${result.evidence.exitStatusKnown}`);
	evidenceLines.push(`exit_status_authoritative: ${result.evidence.exitStatusAuthoritative}`);
	evidenceLines.push(`authority_scope: ${result.evidence.authorityScope}`);
	evidenceLines.push(`internal_command_statuses_known: ${result.evidence.internalCommandStatusesKnown}`);
	evidenceLines.push(`validation_evidence_authoritative: ${result.evidence.validationEvidenceAuthoritative}`);

	if (result.evidence.pipelineSuspected) {
		evidenceLines.push(`pipeline_suspected: true`);
		evidenceLines.push(`stage_exit_codes_known: false`);
		if (result.evidence.warning) {
			evidenceLines.push(`warning: ${result.evidence.warning}`);
		}
	}

	if (result.fullOutputPath && result.truncated) {
		evidenceLines.push(`full_output: ${result.fullOutputPath}`);
	}

	lines.push("");
	lines.push("--- Evidence ---");
	lines.push(evidenceLines.join("\n"));

	const details: ResolvedBashToolDetails = {
		command,
		cwd,
		stdout: result.stdout,
		stderr: result.stderr,
		exitCode: result.exitCode,
		startedAt: result.startedAt,
		finishedAt: result.finishedAt,
		timedOut: result.timedOut,
		cancelled: result.cancelled,
		truncated: result.truncated,
		fullOutputPath: result.fullOutputPath,
		spawnError: result.spawnError,
		evidence: result.evidence,
	};

	return { contentText: lines.join("\n"), details };
}

export function createBashTool(cwd: string, options?: BashToolOptions): AgentTool<typeof bashSchema> {
	const ops = options?.operations ?? createLocalBashOperations();
	const commandPrefix = options?.commandPrefix;
	const spawnHook = options?.spawnHook;

	return {
		name: "bash",
		label: "bash",
		description: `Execute a bash command in the current working directory. Returns stdout and stderr. Output is truncated to last ${DEFAULT_MAX_LINES} lines or ${DEFAULT_MAX_BYTES / 1024}KB (whichever is hit first). If truncated, full output is saved to a temp file. Optionally provide a timeout in seconds.`,
		parameters: bashSchema,
		execute: async (
			_toolCallId: string,
			{ command, timeout: _timeout }: { command: string; timeout?: number },
			signal?: AbortSignal,
			onUpdate?,
		) => {
			// Validate timeout
			if (_timeout !== undefined) {
				if (typeof _timeout !== "number" || Number.isNaN(_timeout) || !Number.isFinite(_timeout)) {
					throw new Error(
						`Invalid timeout value: ${_timeout}. Timeout must be a finite positive number in seconds, or omitted for no timeout.`,
					);
				}
				if (_timeout === 0) {
					throw new Error("Timeout must be a positive number in seconds, or omitted for no timeout. Got 0.");
				}
				if (_timeout < 0) {
					throw new Error(
						`Timeout must be a positive number in seconds, or omitted for no timeout. Got ${_timeout}.`,
					);
				}
				if (_timeout > MAX_TIMEOUT_SECONDS) {
					throw new Error(
						`Timeout value ${_timeout} exceeds maximum allowed timeout of ${MAX_TIMEOUT_SECONDS} seconds.`,
					);
				}
			}

			const resolvedCommand = commandPrefix ? `${commandPrefix}\n${command}` : command;
			const spawnContext = resolveSpawnContext(resolvedCommand, cwd, spawnHook);

			// Stream partial updates via the executor's onChunk callback
			const onChunk = (chunk: string) => {
				if (onUpdate) {
					const truncated = truncateTail(chunk);
					const details: BashToolDetails = {
						command: resolvedCommand,
						cwd: spawnContext.cwd,
						stdout: chunk,
						stderr: "",
						exitCode: undefined,
						startedAt: "",
						finishedAt: "",
						timedOut: false,
						cancelled: false,
						truncated: truncated.truncated,
						truncation: truncated.truncated ? truncated : undefined,
					};
					onUpdate({
						content: [{ type: "text", text: truncated.content || "" }],
						details,
					});
				}
			};

			const result = (await executeBashWithOperations(spawnContext.command, spawnContext.cwd, ops, {
				onChunk,
				signal,
				timeout: _timeout,
			})) as ResolvedBashResult;

			const { contentText, details } = formatBashResultForModel(result, resolvedCommand, spawnContext.cwd);

			// Non-zero exit code, cancelled, timed out, or spawn error: reject so the
			// agent loop marks it isError=true. The error message carries the full
			// structured content so the model sees everything.
			const isError =
				(result.exitCode !== undefined && result.exitCode !== 0 && result.exitCode !== null) ||
				result.cancelled ||
				result.timedOut ||
				result.spawnError !== undefined;

			if (isError) {
				throw new Error(contentText);
			}

			return { content: [{ type: "text", text: contentText }], details };
		},
	};
}

/** Default bash tool using process.cwd() - for backwards compatibility */
export const bashTool = createBashTool(process.cwd());
